"use client";

import { useOpenSupportChat } from "@/helpers/useOpenSupportChat";
import { typeformIds } from "@/lib/consts";
import { useMessages } from "@/lib/i18n/useMessages";
import { typeformPopupButtonStyle } from "@/lib/typeformStyles";
import { buttonPadding } from "@/themeOptions";
import { ArrowOutward, Email, MoreVert } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dynamic from "next/dynamic";
import { useState } from "react";
import { SLCopyToClipboardButton } from "./SLCopyToClipboardButton";

const PopupButton = dynamic(
  () => import("@typeform/embed-react").then((m) => m.PopupButton),
  { ssr: false },
);

export const SLButtonWriteEmail = ({
  email,
  isSolvingTax,
}: {
  email: string;
  isSolvingTax?: boolean;
}) => {
  const messages = useMessages();
  const home = messages.home;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { openChat } = useOpenSupportChat();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "auto",
      }}
    >
      <ButtonGroup
        color="success"
        variant="outlined"
        sx={{
          width: isMobile ? "100%" : "auto",
        }}
      >
        <Button
          size="medium"
          startIcon={<Email fontSize="inherit" />}
          href={`mailto:${email}`}
          sx={{
            width: isMobile ? "100%" : "auto",
          }}
        >
          {home.contact.emailButton}
        </Button>
        <Button
          size="medium"
          onClick={handleClick}
          aria-label={home.contact.moreOptionsAria}
          aria-controls={open ? "write-email-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open}
        >
          <MoreVert fontSize="inherit" aria-hidden="true" />
        </Button>
      </ButtonGroup>
      <Menu
        id="write-email-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: theme.palette.background.default,
            },
          },
        }}
      >
        <MenuItem>
          <SLCopyToClipboardButton
            text={email}
            buttonText={email}
            tooltipText={home.contact.copyButton}
            successMessage={home.contact.copySuccess}
            variant="text"
            sx={{
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
              },
              color: theme.palette.text.primary,
            }}
            size="medium"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          />
        </MenuItem>
        <MenuItem>
          <Button
            color="success"
            variant="text"
            size="medium"
            startIcon={<ArrowOutward />}
            onClick={() => {
              openChat();
            }}
            sx={{
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
              },
              color: theme.palette.text.primary,
              padding: buttonPadding,
            }}
          >
            {messages.dialogs.contactCta.openChat}
          </Button>
        </MenuItem>
        {!isSolvingTax && (
          <MenuItem sx={typeformPopupButtonStyle}>
            <PopupButton id={typeformIds.contact} className="typeform-button">
              {home.contact.contactForm}
            </PopupButton>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
